import { simhashSimilarity } from "./fingerprints";
import type { ClassFingerprint, ScanReport, Severity } from "./types";

export interface ChangedEntry {
  path: string;
  beforeSha256: string;
  afterSha256: string;
  beforeSize: number;
  afterSize: number;
}

export interface SimilarClass {
  beforeClass: string;
  afterClass: string;
  similarity: number;
  beforeEntry: string;
  afterEntry: string;
}

export interface JarComparison {
  identical: boolean;
  archiveSimilarity: number;
  summary: string;
  addedEntries: string[];
  removedEntries: string[];
  changedEntries: ChangedEntry[];
  addedClasses: string[];
  removedClasses: string[];
  changedClasses: SimilarClass[];
  likelyRenamedClasses: SimilarClass[];
  addedActions: Array<{
    id: string;
    title: string;
    severity: Severity;
    entry: string;
  }>;
  removedActions: Array<{
    id: string;
    title: string;
    severity: Severity;
    entry: string;
  }>;
  addedFindings: Array<{
    ruleId: string;
    title: string;
    severity: Severity;
    entry: string;
  }>;
  scoreDelta: number;
}

function fingerprintMap(report: ScanReport) {
  return new Map(report.classFingerprints.map((item) => [item.className, item]));
}

function similarity(left: ClassFingerprint, right: ClassFingerprint) {
  if (left.semanticSha256 === right.semanticSha256) return 100;
  return simhashSimilarity(left.simhash64, right.simhash64);
}

export function compareReports(
  before: ScanReport,
  after: ScanReport,
): JarComparison {
  const beforeEntries = new Map(
    before.entryHashes.map((entry) => [entry.path, entry]),
  );
  const afterEntries = new Map(
    after.entryHashes.map((entry) => [entry.path, entry]),
  );
  const addedEntries = [...afterEntries.keys()]
    .filter((path) => !beforeEntries.has(path))
    .sort();
  const removedEntries = [...beforeEntries.keys()]
    .filter((path) => !afterEntries.has(path))
    .sort();
  const changedEntries = [...beforeEntries.entries()]
    .filter(
      ([path, entry]) =>
        afterEntries.has(path) &&
        afterEntries.get(path)!.sha256 !== entry.sha256,
    )
    .map(([path, entry]) => ({
      path,
      beforeSha256: entry.sha256,
      afterSha256: afterEntries.get(path)!.sha256,
      beforeSize: entry.size,
      afterSize: afterEntries.get(path)!.size,
    }))
    .sort((left, right) => left.path.localeCompare(right.path));

  const beforeClasses = fingerprintMap(before);
  const afterClasses = fingerprintMap(after);
  const addedClasses = [...afterClasses.keys()].filter(
    (name) => !beforeClasses.has(name),
  );
  const removedClasses = [...beforeClasses.keys()].filter(
    (name) => !afterClasses.has(name),
  );
  const changedClasses = [...beforeClasses.entries()]
    .filter(
      ([name, item]) =>
        afterClasses.has(name) &&
        afterClasses.get(name)!.semanticSha256 !== item.semanticSha256,
    )
    .map(([name, item]) => {
      const target = afterClasses.get(name)!;
      return {
        beforeClass: name,
        afterClass: name,
        similarity: similarity(item, target),
        beforeEntry: item.entry,
        afterEntry: target.entry,
      };
    })
    .sort((left, right) => left.similarity - right.similarity);

  const candidates: SimilarClass[] = [];
  for (const oldName of removedClasses) {
    const oldClass = beforeClasses.get(oldName)!;
    for (const newName of addedClasses) {
      const newClass = afterClasses.get(newName)!;
      const score = similarity(oldClass, newClass);
      if (score < 82) continue;
      candidates.push({
        beforeClass: oldName,
        afterClass: newName,
        similarity: score,
        beforeEntry: oldClass.entry,
        afterEntry: newClass.entry,
      });
    }
  }
  const usedBefore = new Set<string>();
  const usedAfter = new Set<string>();
  const likelyRenamedClasses = candidates
    .sort((left, right) => right.similarity - left.similarity)
    .filter((candidate) => {
      if (
        usedBefore.has(candidate.beforeClass) ||
        usedAfter.has(candidate.afterClass)
      )
        return false;
      usedBefore.add(candidate.beforeClass);
      usedAfter.add(candidate.afterClass);
      return true;
    });

  const actionKey = (action: ScanReport["actions"][number]) =>
    `${action.actionId}:${action.entry}:${action.method ?? ""}`;
  const beforeActions = new Map(
    before.actions.map((action) => [actionKey(action), action]),
  );
  const afterActions = new Map(
    after.actions.map((action) => [actionKey(action), action]),
  );
  const addedActions = [...afterActions.entries()]
    .filter(([key]) => !beforeActions.has(key))
    .map(([, action]) => ({
      id: action.actionId,
      title: action.title,
      severity: action.severity,
      entry: action.entry,
    }));
  const removedActions = [...beforeActions.entries()]
    .filter(([key]) => !afterActions.has(key))
    .map(([, action]) => ({
      id: action.actionId,
      title: action.title,
      severity: action.severity,
      entry: action.entry,
    }));
  const beforeFindings = new Set(
    before.findings.map(
      (finding) => `${finding.ruleId}:${finding.entry}:${finding.title}`,
    ),
  );
  const addedFindings = after.findings
    .filter(
      (finding) =>
        !beforeFindings.has(
          `${finding.ruleId}:${finding.entry}:${finding.title}`,
        ),
    )
    .map((finding) => ({
      ruleId: finding.ruleId,
      title: finding.title,
      severity: finding.severity,
      entry: finding.entry,
    }));
  const archiveSimilarity =
    before.hashes.sha256 === after.hashes.sha256
      ? 100
      : simhashSimilarity(
          before.archiveFingerprint.simhash64,
          after.archiveFingerprint.simhash64,
        );
  const identical = before.hashes.sha256 === after.hashes.sha256;
  const changedCount =
    addedEntries.length + removedEntries.length + changedEntries.length;
  return {
    identical,
    archiveSimilarity,
    summary: identical
      ? "The two files are byte-for-byte identical."
      : `${changedCount.toLocaleString()} archive paths changed; normalized behavior is ${archiveSimilarity.toFixed(1)}% similar.`,
    addedEntries,
    removedEntries,
    changedEntries,
    addedClasses: addedClasses.sort(),
    removedClasses: removedClasses.sort(),
    changedClasses,
    likelyRenamedClasses,
    addedActions,
    removedActions,
    addedFindings,
    scoreDelta: after.score - before.score,
  };
}

